import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { invitesApi } from "@/api/invites";
import { ApiException } from "@/api/client";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { TextInput } from "@/components/forms/TextInput";
import { Button } from "@/components/primitives/Button";
import { useSession } from "@/session/SessionProvider";
import { useToast } from "@/components/feedback/Toast";

export default function AcceptInvite() {
	const { code = "" } = useParams();
	const { isAuthed, isLoading, refresh } = useSession();
	const navigate = useNavigate();
	const { toast } = useToast();
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	if (!isLoading && isAuthed) return <Navigate to="/app" replace />;

	if (!code.trim()) {
		return (
			<AuthLayout title="Einladung" subtitle="Kein Einladungscode in der URL.">
				<p className="text-sm text-fg-muted">
					Bitte den vollständigen Link aus der Einladung öffnen.
				</p>
				<Button as="a" href="/login" variant="secondary" className="mt-6">
					Zur Anmeldung
				</Button>
			</AuthLayout>
		);
	}

	async function onSubmit(e: FormEvent) {
		e.preventDefault();
		setBusy(true);
		setError(null);
		try {
			await invitesApi.accept({
				code,
				email,
				name: name.trim() || undefined,
				password,
			});
			await refresh();
			toast("Konto erstellt", "success");
			navigate("/app", { replace: true });
		} catch (err) {
			const message =
				err instanceof ApiException
					? err.message
					: err instanceof Error
						? err.message
						: "Registrierung fehlgeschlagen";
			setError(message);
		} finally {
			setBusy(false);
		}
	}

	return (
		<AuthLayout
			title="Einladung annehmen"
			subtitle="Konto mit Einladungscode anlegen — nur per Einladung."
		>
			<form className="flex flex-col gap-4" onSubmit={onSubmit}>
				<TextInput
					label="Name"
					name="name"
					autoComplete="name"
					value={name}
					onChange={(e) => setName(e.target.value)}
				/>
				<TextInput
					label="E-Mail"
					type="email"
					name="email"
					autoComplete="email"
					required
					value={email}
					onChange={(e) => setEmail(e.target.value)}
				/>
				<TextInput
					label="Passwort"
					type="password"
					name="password"
					autoComplete="new-password"
					minLength={8}
					required
					value={password}
					onChange={(e) => setPassword(e.target.value)}
				/>
				{error ? <p className="text-sm text-rose-500">{error}</p> : null}
				<Button type="submit" disabled={busy}>
					{busy ? "…" : "Konto erstellen"}
				</Button>
			</form>
			<p className="mt-6 text-sm text-fg-muted">
				Bereits registriert?{" "}
				<Link
					className="font-medium text-teal-600 hover:underline dark:text-teal-400"
					to="/login"
				>
					Anmelden
				</Link>
			</p>
		</AuthLayout>
	);
}
